/**
 * GC (ゲートクローズ) カウントダウンの残り時間 表示フォーマット canonical module。
 *
 * 表記は **24 時間未満が `HH:MM:SS` (時も 0 埋め)、 24 時間以上が `N日 HH:MM:SS`**。
 * spec `docs/specs/bidding/intraday-bidding.md` AC-BIN-001 が唯一の明文規定で、
 * 需要想定側の `docs/specs/demand/demand-forecast-common.md` AC-DFC-CD-001 は
 * 「時:分:秒 相当」 としか定めないため、 明文化されている AC-BIN-001 に揃える
 * (issue #4858 でデザイナーが canonical 化 2026-08-14 → issue #4855 で 24 時間以上の
 * `N日` 接頭辞を PdM 判断 2026-08-18)。
 *
 * 画面ごとに整形を持つと「同じバッジなのに画面をまたぐと桁数が変わる」 drift が起きる
 * (実際に需要想定 = `MM:SS` / 時間前入札 = `HH:MM:SS` に割れていた) ため、 両画面の
 * GcCountdown はこの関数だけを使う。 `MM:SS` を採らない理由は、 60 分を超えても分が
 * 繰り上がらず `75:12` (= 1 時間 15 分) を「あと 75 分」 と読ませてしまうため。
 *
 * 「あと」 / 「GC済」 等の文言と、 GC 到来後にどう表示するかは画面側の判断に委ねる
 * (需要想定は固定コマを狙い撃つので「GC済」 が残るが、 時間前入札は境界ごとに次の GC を
 * 取り直すのでその状態にならない = 挙動の違いであって表記の問題ではない)。
 */

const pad2 = (n: number): string => String(n).padStart(2, '0')

/** 1 日の秒数。 24 時間以上を `N日 ` 接頭辞へ繰り上げる境界。 */
const SECONDS_PER_DAY = 24 * 60 * 60

/**
 * 残り秒数を整形する。 **24 時間未満は `HH:MM:SS`、 24 時間以上は `N日 HH:MM:SS`**
 * (例: `3日 05:12:34`)。 負の値 (= GC 経過後) は 0 にクランプして `00:00:00` を返す。
 *
 * `N日` 接頭辞は AC-BIN-001 の明文規定 (issue #4855、 2026-08-18 PdM 判断)。
 * 受渡日の選択範囲に上限が無い (AC-BIN-000a) ため、 遠い受渡日を選ぶと時の桁が
 * 3〜4 桁の巨大な値になり読めなくなる (起点の症状は `5719:42:10`)。
 *
 * スポット入札でも受付開始が受渡日の 11 日前 0:00 (AC-BSP-091) に広がったことで
 * 残り時間が最長 ~250 時間に達し、 同じ症状 (`250:00:00`) が出る条件が揃った
 * (issue #8320 / PR #8344)。 **画面ごとに段階粒度 (「N日M時間」 等) へ振り分けない** —
 * 同じバッジが画面をまたぐと桁が割れる drift を #4858 が canonical 化で潰した経緯がある。
 */
export function formatGcRemainingTime(totalSeconds: number): string {
  const clamped = Math.max(totalSeconds, 0)
  const days = Math.floor(clamped / SECONDS_PER_DAY)
  const withinDay = clamped % SECONDS_PER_DAY
  const hours = Math.floor(withinDay / 3600)
  const minutes = Math.floor((withinDay % 3600) / 60)
  const seconds = withinDay % 60
  const hms = `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}`
  return days > 0 ? `${String(days)}日 ${hms}` : hms
}
